/**
 * NEXUS UI - RadarScan
 * Animated radar sweep with blip markers
 */

import React from 'react';
import type { BaseProps, NexusVariant } from './types';

interface RadarBlip {
  id: string;
  x: number; // 0-100 percentage from center
  y: number; // 0-100 percentage from center
  label?: string;
  status?: 'active' | 'warning' | 'critical';
}

export interface RadarScanProps extends BaseProps {
  blips?: RadarBlip[];
  size?: number;
  variant?: NexusVariant;
  rings?: number;
  sweepSpeed?: number; // seconds per rotation
  sweepEnabled?: boolean; // whether sweep animation is active
  showLabels?: boolean;
  onBlipClick?: (blip: RadarBlip) => void;
}

const variantColors: Record<NexusVariant, { primary: string; glow: string }> = {
  cyan: { primary: '#00d4ff', glow: 'rgba(0, 212, 255, 0.6)' },
  blue: { primary: '#3b82f6', glow: 'rgba(59, 130, 246, 0.6)' },
  violet: { primary: '#a855f7', glow: 'rgba(168, 85, 247, 0.6)' },
  green: { primary: '#00ff88', glow: 'rgba(0, 255, 136, 0.6)' },
  amber: { primary: '#ffaa00', glow: 'rgba(255, 170, 0, 0.6)' },
  red: { primary: '#ff3366', glow: 'rgba(255, 51, 102, 0.6)' },
};

const statusColors = {
  active: { bg: '#00ff88', glow: 'rgba(0, 255, 136, 0.6)' },
  warning: { bg: '#ffaa00', glow: 'rgba(255, 170, 0, 0.6)' },
  critical: { bg: '#ff3366', glow: 'rgba(255, 51, 102, 0.6)' },
};

export const RadarScan: React.FC<RadarScanProps> = ({
  blips = [],
  size = 200,
  variant = 'cyan',
  rings = 4,
  sweepSpeed = 4,
  sweepEnabled = false,
  showLabels = false,
  onBlipClick,
  className = '',
  style,
}) => {
  const colors = variantColors[variant];
  const center = size / 2;
  const maxRadius = (size - 20) / 2;

  return (
    <div
      className={`nx-radar ${className}`}
      style={{
        position: 'relative',
        width: size,
        height: size,
        borderRadius: '50%',
        background: 'var(--nx-void-panel)',
        border: `1px solid rgba(0, 212, 255, 0.3)`,
        overflow: 'hidden',
        ...style,
      }}
    >
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <defs>
          <radialGradient id="radar-sweep-gradient">
            <stop offset="0%" stopColor={colors.primary} stopOpacity="0" />
            <stop offset="50%" stopColor={colors.primary} stopOpacity="0.1" />
            <stop offset="100%" stopColor={colors.primary} stopOpacity="0.3" />
          </radialGradient>
          <filter id="radar-glow">
            <feGaussianBlur stdDeviation="2" result="coloredBlur" />
            <feMerge>
              <feMergeNode in="coloredBlur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {/* Concentric rings */}
        {Array.from({ length: rings }).map((_, i) => {
          const ringRadius = ((i + 1) / rings) * maxRadius;
          return (
            <circle
              key={`ring-${i}`}
              cx={center}
              cy={center}
              r={ringRadius}
              fill="none"
              stroke={colors.primary}
              strokeWidth={1}
              opacity={0.15}
            />
          );
        })}

        {/* Cross hairs */}
        <line
          x1={center}
          y1={10}
          x2={center}
          y2={size - 10}
          stroke={colors.primary}
          strokeWidth={1}
          opacity={0.15}
        />
        <line
          x1={10}
          y1={center}
          x2={size - 10}
          y2={center}
          stroke={colors.primary}
          strokeWidth={1}
          opacity={0.15}
        />

        {/* Diagonal lines */}
        <line
          x1={center - maxRadius * 0.707}
          y1={center - maxRadius * 0.707}
          x2={center + maxRadius * 0.707}
          y2={center + maxRadius * 0.707}
          stroke={colors.primary}
          strokeWidth={1}
          opacity={0.08}
        />
        <line
          x1={center + maxRadius * 0.707}
          y1={center - maxRadius * 0.707}
          x2={center - maxRadius * 0.707}
          y2={center + maxRadius * 0.707}
          stroke={colors.primary}
          strokeWidth={1}
          opacity={0.08}
        />

        {/* Sweep line with trail */}
        <g
          style={{
            transformOrigin: `${center}px ${center}px`,
            animation: sweepEnabled ? `nx-rotate ${sweepSpeed}s linear infinite` : 'none',
          }}
        >
          {/* Trail gradient */}
          <path
            d={`M ${center} ${center} L ${center} ${10} A ${maxRadius} ${maxRadius} 0 0 1 ${center + maxRadius * 0.866} ${center - maxRadius * 0.5} Z`}
            fill="url(#radar-sweep-gradient)"
            opacity={0.5}
          />
          {/* Main sweep line */}
          <line
            x1={center}
            y1={center}
            x2={center}
            y2={10}
            stroke={colors.primary}
            strokeWidth={2}
            filter="url(#radar-glow)"
          />
        </g>

        {/* Center dot */}
        <circle
          cx={center}
          cy={center}
          r={4}
          fill={colors.primary}
          filter="url(#radar-glow)"
        />

        {/* Blips */}
        {blips.map((blip) => {
          const blipX = center + (blip.x / 100) * maxRadius;
          const blipY = center + (blip.y / 100) * maxRadius;
          const blipColors = statusColors[blip.status || 'active'];

          return (
            <g
              key={blip.id}
              onClick={() => onBlipClick?.(blip)}
              style={{ cursor: onBlipClick ? 'pointer' : 'default' }}
            >
              {/* Blip outer ring - static, no animation */}
              <circle
                cx={blipX}
                cy={blipY}
                r={8}
                fill="none"
                stroke={blipColors.bg}
                strokeWidth={1}
                opacity={0.4}
              />
              {/* Blip dot - only critical pulses to demand attention */}
              <circle
                cx={blipX}
                cy={blipY}
                r={4}
                fill={blipColors.bg}
                filter="url(#radar-glow)"
                style={{
                  animation: blip.status === 'critical' ? 'nx-pulse 0.8s infinite' : 'none',
                }}
              />
              {/* Blip label */}
              {showLabels && blip.label && (
                <text
                  x={blipX + 8}
                  y={blipY + 4}
                  fill={blipColors.bg}
                  fontSize="9"
                  fontFamily="var(--nx-font-mono)"
                >
                  {blip.label}
                </text>
              )}
            </g>
          );
        })}
      </svg>

      {/* Outer glow ring */}
      <div
        style={{
          position: 'absolute',
          inset: -1,
          borderRadius: '50%',
          border: `1px solid ${colors.primary}`,
          opacity: 0.3,
          pointerEvents: 'none',
          boxShadow: `0 0 20px ${colors.glow}`,
        }}
      />
    </div>
  );
};

export default RadarScan;
